import { getAccessToken, useAuthStore } from "@/stores/auth";

export class ApiError extends Error {
  readonly status: number;
  readonly detail: unknown;

  constructor(status: number, detail: unknown, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.detail = detail;
  }
}

interface ApiFetchOptions extends Omit<RequestInit, "body"> {
  body?: unknown;
  form?: Record<string, string>;
  auth?: boolean;
}

const API_PREFIX = "/api";

export async function apiFetch<T = unknown>(
  path: string,
  options: ApiFetchOptions = {},
): Promise<T> {
  const { body, form, auth = true, headers, ...rest } = options;

  const finalHeaders = new Headers(headers);
  let payload: BodyInit | undefined;

  if (form) {
    payload = new URLSearchParams(form).toString();
    finalHeaders.set("Content-Type", "application/x-www-form-urlencoded");
  } else if (body !== undefined) {
    payload = JSON.stringify(body);
    finalHeaders.set("Content-Type", "application/json");
  }

  if (auth) {
    const token = getAccessToken();
    if (token) {
      finalHeaders.set("Authorization", `Bearer ${token}`);
    }
  }

  const init: RequestInit = {
    ...rest,
    headers: finalHeaders,
  };
  if (payload !== undefined) init.body = payload;

  const response = await fetch(`${API_PREFIX}${path}`, init);

  if (response.status === 204) {
    return undefined as T;
  }

  const contentType = response.headers.get("content-type") ?? "";
  const parsed = contentType.includes("application/json")
    ? await response.json()
    : await response.text();

  if (!response.ok) {
    if (response.status === 401 && auth) {
      useAuthStore.getState().clearToken();
    }
    const message =
      (typeof parsed === "object" && parsed !== null && "detail" in parsed
        ? extractDetailMessage((parsed as { detail: unknown }).detail)
        : null) ?? response.statusText;
    throw new ApiError(response.status, parsed, message);
  }

  return parsed as T;
}

function extractDetailMessage(detail: unknown): string | null {
  if (typeof detail === "string") return detail;
  if (
    typeof detail === "object" &&
    detail !== null &&
    "message" in detail &&
    typeof (detail as { message: unknown }).message === "string"
  ) {
    return (detail as { message: string }).message;
  }
  return null;
}

export function isMustChangePasswordError(err: unknown): boolean {
  if (!(err instanceof ApiError) || err.status !== 403) return false;
  const detail = err.detail;
  if (
    typeof detail === "object" &&
    detail !== null &&
    "detail" in detail &&
    typeof (detail as { detail: unknown }).detail === "object" &&
    (detail as { detail: { code?: unknown } }).detail !== null
  ) {
    const inner = (detail as { detail: { code?: unknown } }).detail;
    return inner.code === "MUST_CHANGE_PASSWORD";
  }
  return false;
}
